import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Spin, Button } from "antd";
import { LoadingOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { useHandleShopifyCallbackMutation } from "@/api/shopifyApi";
import AuthService from "@/auth";

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
`;

const CardContainer = styled.div`
  background: white;
  padding: 48px 32px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  text-align: center;
  min-width: 400px;
`;

export const ShopifyCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [handleCallback] = useHandleShopifyCallbackMutation();
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      try {
        const code = searchParams.get("code");
        const shop = searchParams.get("shop");
        const errorParam = searchParams.get("error");

        if (errorParam) {
          setStatus("error");
          setError(
            searchParams.get("error_description") ||
            "Authorization was denied"
          );
          return;
        }

        if (!code || !shop) {
          setStatus("error");
          setError("Missing authorization code or shop parameter");
          return;
        }

        // Get userId from auth cache
        const user = AuthService.getUser();
        const userId = user?.id;

        if (!userId) {
          setStatus("error");
          setError("User session expired. Please login again.");
          return;
        }

        await handleCallback({ code, shop, userId }).unwrap();
        setStatus("success");

        // Redirect after 2 seconds to Connected Stores tab
        setTimeout(() => {
          navigate("/profile?tab=connectedStores");
        }, 2000);
      } catch (err) {
        setStatus("error");
        setError(err?.data?.message || "Failed to complete authorization");
      }
    };

    processCallback();
  }, [handleCallback, navigate, searchParams]);

  return (
    <Container>
      <CardContainer>
        {status === "loading" && (
          <>
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 48 }} />}
              style={{ marginBottom: "24px" }}
            />
            <h2>Connecting to Shopify...</h2>
            <p style={{ color: "#999" }}>
              Please wait while we complete the authorization.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircleOutlined
              style={{ fontSize: 48, color: "#52c41a", marginBottom: "24px" }}
            />
            <h2>Connected!</h2>
            <p style={{ color: "#52c41a", marginBottom: "16px" }}>
              Your Shopify store has been connected successfully.
            </p>
            <p style={{ color: "#999", fontSize: "12px" }}>
              Redirecting to dashboard...
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <CloseCircleOutlined
              style={{ fontSize: 48, color: "#ff4d4f", marginBottom: "24px" }}
            />
            <h2>Connection Failed</h2>
            <p style={{ color: "#999", marginBottom: "16px" }}>{error}</p>
            <Button type="primary" onClick={() => navigate("/dashboard")}>
              Back to Dashboard
            </Button>
          </>
        )}
      </CardContainer>
    </Container>
  );
};
